import React, { Component } from 'react'

const MOUSE_LOCS_TRACKED = 3
const TOLERANCE = 75
const DELAY = 300

const withMouseMenuAim = (WrappedComponent, config = {}) => {
  const offset = el => {
    if (!el) return { left: 0, top: 0 }
    const { top, left } = el.getBoundingClientRect()
    const style = el.currentStyle || getComputedStyle(el)
    return {
      top: top + document.body.scrollTop,
      left: left + document.body.scrollLeft,
      width: el.offsetWidth + (parseInt(style.marginLeft, 10) || 0),
      height: el.offsetHeight + (parseInt(style.marginLeft, 10) || 0)
    }
  }
  const slope = (a, b) => (b.y - a.y) / (b.x - a.x)
  return class extends Component {
    constructor (props) {
      super(props)
      this.state = {
        mouseLocs: [],
        mousemoveListener: 0,
        lastDelayLocation: null,
        timer: null
      }
      this.wrappedComponentRef = React.createRef()
    }

    getMenuCorners = ({ top, left, width, height }) => {
      const upperLeft = { x: left, y: top - (config.tolerance || TOLERANCE) }
      const upperRight = { x: left + width, y: upperLeft.y }
      const lowerLeft = { x: left, y: top + height + (config.tolerance || TOLERANCE) }
      const lowerRight = { x: left + width, y: lowerLeft.y }
      return { upperLeft, upperRight, lowerLeft, lowerRight }
    }

    handleMouseMovement = e => {
      const { pageX, pageY } = e
      const { mouseLocs } = this.state
      const prevLocations = mouseLocs.length + 1 > MOUSE_LOCS_TRACKED ? mouseLocs.slice(1) : mouseLocs
      this.setState({ mouseLocs: [...prevLocations, { x: pageX, y: pageY }] })
    }

    getActivateDelay = () => {
      if (!this.menuData || !this.menuCorners) return 0
      const { top, left } = this.menuData
      const { upperRight, lowerRight } = this.menuCorners

      const { mouseLocs } = this.state
      const loc = mouseLocs[mouseLocs.length - 1]
      const [ prevLoc = loc ] = mouseLocs

      const outsideMenu = prevLoc.x < left || prevLoc.x > lowerRight.x || prevLoc.y < top || prevLoc.y > lowerRight.y
      const { lastDelayLocation } = this.state
      const noMovement = lastDelayLocation && loc.x === lastDelayLocation.x && loc.y === lastDelayLocation.y
      if (!loc || outsideMenu || noMovement) return 0

      const decreasingSlope = slope(loc, upperRight)
      const increasingSlope = slope(loc, lowerRight)
      const prevDecreasingSlope = slope(prevLoc, upperRight)
      const prevIncreasingSlope = slope(prevLoc, lowerRight)

      if (decreasingSlope < prevDecreasingSlope && increasingSlope > prevIncreasingSlope) {
        this.setState({ lastDelayLocation: loc })
        return config.delay || DELAY
      }
      this.setState({ lastDelayLocation: null })
      return 0
    }

    possiblyActivate = (handler, waiting = false) => {
      if (waiting) return
      const delay = this.getActivateDelay()
      if (delay) {
        const timer = setTimeout(() => this.possiblyActivate(handler, true), delay)
        this.setState({ timer })
        return
      }
      handler()
    }

    handleMouseEnterOption = handler => {
      if (this.timer) clearTimeout(this.timer)
      this.possiblyActivate(handler)
    }

    componentDidMount () {
      const menu = this.wrappedComponentRef.current
      if (menu) {
        this.menuData = offset(menu)
        this.menuCorners = this.getMenuCorners(this.menuData)
      }
      /* eslint-disable-next-line react/destructuring-assignment */
      if (this.state.mousemoveListener === 0) document.addEventListener('mousemove', this.handleMouseMovement)
      this.setState(prevState => ({ mousemoveListener: prevState.mousemoveListener + 1 }))
    }

    componentWillUnmount () {
      document.removeEventListener('mousemove', this.handleMouseMovement)
      clearTimeout(this.timer)
      this.timer = null
      this.mouseMoveHandler = null
    }

    render () {
      return <WrappedComponent
        ref={this.wrappedComponentRef}
        handleMouseEnterOption={this.handleMouseEnterOption}
        {...this.props} />
    }
  }
}

export default withMouseMenuAim